/**
 * designSeed — the ONE client-side composition of a pass-design SEED from a
 * (service, flight): wallet-core `serviceToDesign` plus the template's own
 * relabelling. MIRROR of the server's seed paths (partner-portal-api
 * services/passDesignSamples.ts `designSeedFor` for the sample seed, and the
 * Apple mint's design-path seed for a real pass) — the frontend must not
 * import server code, so keep the two in sync.
 *
 * Two callers, so they can never drift: the mock backend's GET-one seed
 * (sample service + sample flight) and the pass page's preview (the pass's
 * own service + flight).
 */
import type {
  AppleStyle,
  FlightContext,
  PassDesign,
  PassDesignField,
  ServicePayload,
} from '@insurwiz/wallet-core';
import { serviceToDesign } from '@insurwiz/wallet-core';

import type { PassTemplateKind } from '../types';

/**
 * De-lounge a freshly-seeded assistance PassDesign — MIRROR of the server's
 * generalizeAssistanceSeed (apps/partner-portal-api/src/services/
 * assistanceContent.ts; the frontend must not import server code — keep the
 * two in sync). The lounge render vehicle's KEYS stay (the applyLiveData
 * [LIVE] bridge); only the labels and the title/logoText change: fields keyed
 * 'lounge'/'lounge_code'/'access_window' get the general-insurance labels,
 * and the primary `title` value + `logoText` become the service NAME (the
 * 'lounge'-keyed field's value) instead of 'Lounge Access'.
 */
export function generalizeAssistanceSeed(seed: PassDesign): PassDesign {
  const labels: Record<string, string> = {
    lounge: 'Szolgáltatás',
    lounge_code: 'Azonosító',
    access_window: 'Érvényesség',
  };
  const serviceName =
    [...(seed.fields.back ?? []), ...(seed.fields.secondary ?? [])].find((f) => f.key === 'lounge')
      ?.value ?? '';
  const fields: PassDesign['fields'] = {};
  for (const group of ['header', 'primary', 'secondary', 'auxiliary', 'back'] as const) {
    const list = seed.fields[group];
    if (list === undefined) continue;
    fields[group] = list.map((field) => {
      const label = labels[field.key];
      if (label !== undefined) return { ...field, label };
      if (field.key === 'title' && serviceName !== '') return { ...field, value: serviceName };
      return { ...field };
    });
  }
  const out: PassDesign = { ...seed, fields };
  if (serviceName !== '') out.logoText = serviceName;
  return out;
}

// ── Travel: Hungarian travel-insurance framing ────────────────────────────────
// MIRROR of apps/partner-portal-api/src/services/travelContent.ts — keep the
// two in sync. The lounge service kind is only the travel pass's render
// vehicle: its title, fabricated '<origin> Lounge' row, lounge code and
// access window never belong on a travel-insurance pass.

/** The pass title (logoText) — the travel product, not a lounge. */
export const TRAVEL_TITLE = 'Utasbiztosítás';

/** The sample passenger of the design seed (the studio canvas). */
export const TRAVEL_SAMPLE_PASSENGER = 'Minta Utas';

const TRAVEL_LABELS: Readonly<Record<string, string>> = {
  flight: 'JÁRAT',
  origin: 'HONNAN',
  destination: 'HOVÁ',
  passenger: 'UTAS',
  departure: 'INDULÁS',
  gate: 'KAPU',
  status: 'STÁTUSZ',
  terminal: 'TERMINÁL',
};
const TRAVEL_GATE_CHANGE_MESSAGE = 'Kapu: %@';
/** Lounge render-vehicle rows that carry only fabricated data on a travel pass. */
const FABRICATED_KEYS = new Set(['lounge_code', 'access_window']);
const FIELD_GROUPS = ['header', 'primary', 'secondary', 'auxiliary', 'back'] as const;

const LEGACY_TITLE = 'Lounge Access';
const LEGACY_LABELS: Readonly<Record<string, string>> = {
  flight: 'FLIGHT',
  lounge: 'Lounge',
  departure: 'Departure',
  gate: 'Gate',
  status: 'Status',
  terminal: 'Terminal',
};
const LEGACY_GATE_CHANGE_MESSAGE = 'Gate: %@';
/** The old seed labelled origin/destination with the SAMPLE flight's IATA codes. */
const IATA_LABEL = /^[A-Z]{3}$/;

/** Re-frame a freshly seeded travel design (pure). Keys stay, except lounge → passenger. */
export function travelizeSeed(seed: PassDesign, passengerName: string): PassDesign {
  const fields: PassDesign['fields'] = {};
  for (const group of FIELD_GROUPS) {
    const list = seed.fields[group];
    if (list === undefined) continue;
    const out: PassDesignField[] = [];
    for (const field of list) {
      if (FABRICATED_KEYS.has(field.key)) continue;
      if (field.key === 'lounge') {
        out.push({ key: 'passenger', label: TRAVEL_LABELS['passenger']!, value: passengerName });
        continue;
      }
      const label = TRAVEL_LABELS[field.key];
      const next: PassDesignField = label !== undefined ? { ...field, label } : { ...field };
      if (field.key === 'gate') next.changeMessage = TRAVEL_GATE_CHANGE_MESSAGE;
      out.push(next);
    }
    fields[group] = out;
  }
  return { ...seed, fields, logoText: TRAVEL_TITLE };
}

/**
 * Heal a travel design saved from the old (lounge, English) seed — pure, and
 * the SAME object back when there is nothing to heal. Only exact legacy
 * defaults are rewritten; the fabricated lounge rows are dropped; a `lounge`
 * row is re-keyed to `passenger` so the mint fills in the traveller.
 */
export function normalizeLegacyTravelDesign(design: PassDesign): PassDesign {
  let changed = false;
  const hasPassenger = FIELD_GROUPS.some((g) =>
    (design.fields[g] ?? []).some((f) => f.key === 'passenger'),
  );
  const fields: PassDesign['fields'] = {};
  for (const group of FIELD_GROUPS) {
    const list = design.fields[group];
    if (list === undefined) continue;
    const out: PassDesignField[] = [];
    for (const field of list) {
      if (FABRICATED_KEYS.has(field.key)) {
        changed = true;
        continue;
      }
      if (field.key === 'lounge') {
        changed = true;
        if (hasPassenger) continue; // a stray duplicate — the design already has the row
        const label =
          field.label === undefined || field.label === LEGACY_LABELS['lounge']
            ? TRAVEL_LABELS['passenger']!
            : field.label;
        out.push({ ...field, key: 'passenger', label, value: TRAVEL_SAMPLE_PASSENGER });
        continue;
      }
      let next = field;
      const legacyLabel = LEGACY_LABELS[field.key];
      if (legacyLabel !== undefined && field.label === legacyLabel) {
        next = { ...next, label: TRAVEL_LABELS[field.key]! };
      }
      if (
        (field.key === 'origin' || field.key === 'destination') &&
        field.label !== undefined &&
        IATA_LABEL.test(field.label)
      ) {
        next = { ...next, label: TRAVEL_LABELS[field.key]! };
      }
      if (field.key === 'gate' && field.changeMessage === LEGACY_GATE_CHANGE_MESSAGE) {
        next = { ...next, changeMessage: TRAVEL_GATE_CHANGE_MESSAGE };
      }
      if (next !== field) changed = true;
      out.push(next);
    }
    fields[group] = out;
  }
  const logoText = design.logoText === LEGACY_TITLE ? TRAVEL_TITLE : design.logoText;
  if (logoText !== design.logoText) changed = true;
  if (!changed) return design;
  const out: PassDesign = { ...design, fields };
  if (logoText !== undefined) out.logoText = logoText;
  return out;
}

/**
 * serviceToDesign + the template's re-framing: the general template is
 * de-lounged, the travel template gets its Hungarian travel-insurance framing
 * with `passengerName` on the front (the sample passenger on the studio seed,
 * the pass's recipient on the pass-page preview).
 */
export function composeSeed(
  templateKind: PassTemplateKind,
  appleStyle: AppleStyle,
  service: ServicePayload,
  flight: FlightContext,
  passengerName: string = TRAVEL_SAMPLE_PASSENGER,
): PassDesign {
  const seed = serviceToDesign(service, flight, appleStyle);
  if (templateKind === 'assistance') return generalizeAssistanceSeed(seed);
  if (templateKind === 'travel') return travelizeSeed(seed, passengerName);
  return seed;
}
